/**
 * Browser PCM audio capture for real-time (streaming) transcription.
 *
 * Captures raw PCM 16-bit LE mono at 16 kHz using AudioContext + ScriptProcessorNode
 * and emits chunks LIVE via an onChunk callback (as the user speaks) — so the
 * consumer can feed AWS Transcribe streaming directly and surface partials.
 *
 * ScriptProcessorNode is deprecated but works in Chrome, Firefox, Safari, Edge.
 * AudioWorklet would be cleaner but needs a separate file + has Safari edge cases;
 * deferred (documented tech-debt). Ported from KinGuardian's pcm-capture.ts; the
 * one change vs KG is the live onChunk callback instead of accumulate-then-blob.
 *
 * Audio stays in our AWS account — no Google / no third-party STT
 * (see memory: feedback_voice_capture_privacy).
 */

const TARGET_SAMPLE_RATE = 16000;
// 2048 samples per buffer = ~128 ms at 16 kHz — snappy partials without spamming.
const BUFFER_SIZE = 2048;

export interface StreamCaptureHandle {
  /** Stop the mic + audio graph. Safe to call once. */
  stop: () => void;
}

type WindowAudioContext = typeof AudioContext;

function getAudioContextCtor(): WindowAudioContext | null {
  if (typeof window === 'undefined') return null;
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: WindowAudioContext }).webkitAudioContext ||
    null
  );
}

/** True if this browser can capture mic PCM for streaming. */
export function isPcmCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    !!getAudioContextCtor()
  );
}

/**
 * Start capturing microphone audio as raw PCM, emitting Uint8Array chunks
 * (16-bit LE mono 16 kHz) via onChunk as they arrive.
 *
 * Requests mic permission, sets up AudioContext + ScriptProcessorNode, and
 * resamples if the browser's native rate differs from 16 kHz.
 */
export async function startStreamCapture(
  onChunk: (pcm: Uint8Array) => void,
): Promise<StreamCaptureHandle> {
  const AudioCtx = getAudioContextCtor();
  if (!AudioCtx || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('PCM capture not supported in this browser');
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      sampleRate: TARGET_SAMPLE_RATE,
      echoCancellation: true,
      noiseSuppression: true,
    },
  });

  const audioContext = new AudioCtx({ sampleRate: TARGET_SAMPLE_RATE });
  // iOS Safari can start the context suspended until a user gesture; the mic
  // tap is the gesture, so resume() here is safe + needed.
  if (audioContext.state === 'suspended') {
    try { await audioContext.resume(); } catch { /* best-effort */ }
  }

  const source = audioContext.createMediaStreamSource(stream);
  const nativeSampleRate = audioContext.sampleRate;
  const processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);

  let stopped = false;

  processor.onaudioprocess = (event: AudioProcessingEvent) => {
    if (stopped) return;
    const inputData = event.inputBuffer.getChannelData(0);
    const samples =
      nativeSampleRate !== TARGET_SAMPLE_RATE
        ? resample(inputData, nativeSampleRate, TARGET_SAMPLE_RATE)
        : inputData;
    const int16 = float32ToInt16(samples);
    // Emit the bytes (a copy — Transcribe consumes the buffer downstream).
    onChunk(new Uint8Array(int16.buffer.slice(0)));
  };

  source.connect(processor);
  processor.connect(audioContext.destination);

  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      try { processor.disconnect(); } catch { /* noop */ }
      try { source.disconnect(); } catch { /* noop */ }
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      audioContext.close().catch(() => {});
    },
  };
}

/** Convert Float32Array samples (-1.0..1.0) to Int16Array (16-bit LE). */
export function float32ToInt16(float32: Float32Array): Int16Array {
  const int16 = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const clamped = Math.max(-1, Math.min(1, float32[i]));
    int16[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return int16;
}

/** Linear-interpolation resampler (e.g. 48000 / 44100 -> 16000). */
export function resample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outputLength = Math.round(input.length / ratio);
  const output = new Float32Array(outputLength);
  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i * ratio;
    const srcFloor = Math.floor(srcIndex);
    const srcCeil = Math.min(srcFloor + 1, input.length - 1);
    const frac = srcIndex - srcFloor;
    output[i] = input[srcFloor] * (1 - frac) + input[srcCeil] * frac;
  }
  return output;
}
